// frontend/src/pages/TeacherVideoDetail.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { computeTotalWatchTime } from "../utils/watchUtils";
import { API_URL } from "../config";
import {
  Container,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  Button as MuiButton,
  useTheme,
  Paper,
  Grid,
  Divider,
  Chip,
  Tooltip,
  LinearProgress,
  ListItemText,
} from "@mui/material";
import {
  PlayCircleOutline,
  PauseCircleOutline,
  SkipNext,
  EditNote,
  StopCircleOutlined,
  ReplayOutlined,
  Assessment,
  Group,
  Notes,
} from "@mui/icons-material";
import WatchGanttChart from "../components/WatchGanttChart";

const formatTime = (secs) => {
  if (secs === null || secs === undefined || isNaN(secs)) return "N/A";
  if (secs === 0) return "0s";
  const h = Math.floor(secs / 3600),
    m = Math.floor((secs % 3600) / 60),
    s = Math.floor(secs % 60);
  return `${h > 0 ? h + "h " : ""}${m > 0 ? m + "m " : ""}${s}s`;
};

const formatTimestampJST = (iso) => {
  if (!iso) return "N/A";
  try {
    const utcIso = iso.endsWith("Z") ? iso : iso + "Z";
    const date = new Date(utcIso);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleString("ja-JP", {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch (e) {
    console.error("Error formatting timestamp in TeacherVideoDetail:", iso, e);
    return "N/A";
  }
};

function renderOperation(ev, prevEv) {
  const pos = ev.played_seconds ?? 0;
  let icon = null;
  let text = "";
  let chipColor = "default";

  switch (ev.event_type) {
    case "start":
      icon = <PlayCircleOutline sx={{ verticalAlign: "middle", mr: 0.5 }} />;
      text = `再生開始 @ ${formatTime(pos)}`;
      chipColor = "success";
      break;
    case "resume":
      if (
        prevEv &&
        prevEv.event_type === "pause" &&
        prevEv.played_seconds !== ev.played_seconds
      ) {
        icon = <SkipNext sx={{ verticalAlign: "middle", mr: 0.5 }} />;
        text = `シーク後に再開 @ ${formatTime(pos)}`;
      } else {
        icon = <ReplayOutlined sx={{ verticalAlign: "middle", mr: 0.5 }} />;
        text = `再開 @ ${formatTime(pos)}`;
      }
      chipColor = "info";
      break;
    case "pause":
      icon = <PauseCircleOutline sx={{ verticalAlign: "middle", mr: 0.5 }} />;
      text = `一時停止 @ ${formatTime(pos)}`;
      chipColor = "warning";
      break;
    case "seek":
      icon = <SkipNext sx={{ verticalAlign: "middle", mr: 0.5 }} />;
      text = `シーク @ ${formatTime(pos)}`;
      chipColor = "default";
      break;
    case "end":
      icon = <StopCircleOutlined sx={{ verticalAlign: "middle", mr: 0.5 }} />;
      text = `再生終了 @ ${formatTime(pos)}`;
      chipColor = "error";
      break;
    case "note":
      icon = <EditNote sx={{ verticalAlign: "middle", mr: 0.5 }} />;
      text = `ノート @ ${formatTime(pos)}`;
      chipColor = "primary";
      if (ev.note_text) {
        return (
          <Tooltip title={ev.note_text} placement="top-start">
            <Box component="span" display="flex" alignItems="center">
              {icon}
              <Chip label={ev.event_type} size="small" color={chipColor} sx={{ mr: 1 }} />
              {text}: {ev.note_text.substring(0, 30)}{ev.note_text.length > 30 ? "..." : ""}
            </Box>
          </Tooltip>
        );
      }
      break;
    default:
      text = `${ev.event_type} @ ${formatTime(pos)}`;
  }
  return (
    <Box component="span" display="flex" alignItems="center">
      {icon}
      <Chip label={ev.event_type} size="small" color={chipColor} sx={{ mr: 1 }}/>
      {text}
    </Box>
  );
}

const groupByStudent = (events) => {
  if (!events || !Array.isArray(events)) return {};
  return events.reduce((map, ev) => {
    if (ev && ev.student_id !== undefined) {
      (map[ev.student_id] ||= []).push(ev);
    }
    return map;
  }, {});
};

export default function TeacherVideoDetail() {
  const { videoId } = useParams();
  const token = localStorage.getItem("token");
  const theme = useTheme();

  const [allEventsForVideo, setAllEventsForVideo] = useState([]);
  const [groupedByStudent, setGroupedByStudent] = useState({});
  const [selectedStudentId, setSelectedStudentId] = useState(""); // student_id (数値)
  const [message, setMessage] = useState("");
  const [videoInfo, setVideoInfo] = useState({ title: "", duration: null });

  useEffect(() => {
    if (!token || !videoId) return;
    axios
      .get(`${API_URL}/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const duration = parseFloat(res.data.duration);
        setVideoInfo({ title: res.data.title, duration: isNaN(duration) ? null : duration });
      })
      .catch(() => setMessage("動画情報の取得に失敗しました。"));
  }, [videoId, token]);

  const fetchLogs = useCallback(async () => {
    if (!token || !videoId) {
      setMessage("認証トークンまたは動画IDがありません。");
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/teacher/logs/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const eventsData = Array.isArray(res.data) ? res.data : [];
      setAllEventsForVideo(eventsData);
      setGroupedByStudent(groupByStudent(eventsData));
      setMessage("");
    } catch (err) {
      console.error("TeacherVideoDetail: 視聴ログの取得エラー:", err);
      setMessage(err.response?.data?.detail || err.message || "ログの取得に失敗しました。");
    }
  }, [token, videoId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);
  
  const videoWideSummary = useMemo(() => {
    if (allEventsForVideo.length === 0) return null;
    const studentIds = new Set(allEventsForVideo.map((e) => e.student_id));
    let totalWatchedTimeAcrossStudents = 0;
    let studentsWithProgress = 0;
    let totalProgress = 0;
    let totalNotes = 0;

    studentIds.forEach((id) => {
      const studentEvents = allEventsForVideo.filter((e) => e.student_id === id);
      if (studentEvents.length > 0) {
        const studentWatchTime = computeTotalWatchTime(studentEvents);
        totalWatchedTimeAcrossStudents += studentWatchTime;
        if (videoInfo.duration && studentWatchTime > 0 && videoInfo.duration > 0) {
          studentsWithProgress++;
          totalProgress += Math.min(100, (studentWatchTime / videoInfo.duration) * 100);
        }
        totalNotes += studentEvents.filter((e) => e.event_type === "note").length;
      }
    });
    const validViewingStudents = studentIds.size > 0 ? studentIds.size : 1;

    return {
      viewingStudentsCount: studentIds.size,
      averageWatchTime: totalWatchedTimeAcrossStudents / validViewingStudents,
      averageProgress: studentsWithProgress > 0 ? totalProgress / studentsWithProgress : 0,
      totalNotesCount: totalNotes,
    };
  }, [allEventsForVideo, videoInfo.duration]);

  const studentOptions = useMemo(
    () =>
      Object.entries(groupedByStudent)
        .filter(([, evs]) => evs && evs.length > 0)
        .map(([sid, evs]) => {
          const firstEvent = evs[0];
          const studentName = firstEvent.student_full_name || `学生 ${sid}`;
          const studentEmailPart = firstEvent.student_email?.split("@")[0] || sid.toString();
          const totalTime = computeTotalWatchTime(evs);
          const progress =
            videoInfo.duration && totalTime > 0 && videoInfo.duration > 0
              ? Math.min( 100, Math.round((totalTime / videoInfo.duration) * 100))
              : 0;
          return { value: sid, label: `${studentName} (${studentEmailPart}) - ${formatTime(totalTime)} (${progress}%)`, name: studentName, totalTime: totalTime, };
        })
        .sort((a, b) => b.totalTime - a.totalTime), 
    [groupedByStudent, videoInfo.duration]
  );

  const selectedStudentEvents = selectedStudentId && groupedByStudent[selectedStudentId]
    ? (groupedByStudent[selectedStudentId] || []).sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )
    : [];

  const selectedStudentTotalTime = useMemo(() => {
      return selectedStudentEvents.length > 0 ? computeTotalWatchTime(selectedStudentEvents) : 0;
  }, [selectedStudentEvents]);

  const selectedStudentProgress = useMemo(() => {
    return videoInfo.duration && selectedStudentTotalTime > 0 && videoInfo.duration > 0
      ? Math.min(100, Math.round((selectedStudentTotalTime / videoInfo.duration) * 100))
      : 0;
  }, [selectedStudentTotalTime, videoInfo.duration]);
  
  const selectedStudentNotes = useMemo(() => {
      return selectedStudentEvents.filter((e) => e.event_type === "note");
  }, [selectedStudentEvents]);
  
  const lastViewedTs = useMemo(() => {
    return selectedStudentEvents.length > 0
    ? new Date( Math.max( ...selectedStudentEvents.map((e) => e.timestamp ? new Date(e.timestamp.endsWith("Z") ? e.timestamp : e.timestamp + "Z").getTime() : 0).filter(ts => !isNaN(ts)) ))
    : null;
  }, [selectedStudentEvents]);

  const currentVideoDurationForGantt = videoInfo.duration && typeof videoInfo.duration === 'number' && videoInfo.duration > 0 ? videoInfo.duration : null;
  const showGantt = selectedStudentEvents.length > 0 && currentVideoDurationForGantt;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2 }}>
        <MuiButton variant="outlined" component={Link} to="/teacher/video-upload">動画一覧へ戻る</MuiButton>
        <MuiButton variant="contained" onClick={fetchLogs} startIcon={<ReplayOutlined />}>ログ更新</MuiButton>
      </Box>

      <Paper elevation={3} sx={{ p: 3, backgroundColor: "#000", borderRadius: 2, color: "#fff" }}>
        <Typography variant="h5" gutterBottom sx={{color: "#fff"}}>{videoInfo.title || `動画ID: ${videoId}`} の視聴状況</Typography>
        {videoInfo.duration !== null && (<Typography variant="subtitle1" color="text.secondary" gutterBottom>動画の長さ: {formatTime(videoInfo.duration)}</Typography>)}
        {message && ( <Typography color="error" sx={{ mb: 2 }}> {message} </Typography> )}

        {videoWideSummary && !message && (
          <Box sx={{ mb: 3, p: 2, border: `1px solid ${theme.palette.grey[700]}`, borderRadius: 1, backgroundColor: "#1e1e1e", color: "#fff" }}>
            <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", color: "#fff" }}><Assessment sx={{ mr: 1, color: theme.palette.primary.main }} />全体サマリー</Typography>
            <Grid container spacing={1}>
              <Grid item xs={6} sm={3}><Typography sx={{color: "#fff"}}>視聴学生数: {videoWideSummary.viewingStudentsCount}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography sx={{color: "#fff"}}>平均視聴時間: {formatTime(videoWideSummary.averageWatchTime)}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography sx={{color: "#fff"}}>平均進捗率: {videoWideSummary.averageProgress.toFixed(1)}%</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography sx={{color: "#fff"}}>合計ノート数: {videoWideSummary.totalNotesCount}</Typography></Grid>
            </Grid>
          </Box>
        )}

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="student-select-label" sx={{color: theme.palette.grey[400]}}>学生を選択 (名前 - 合計視聴時間 (進捗率))</InputLabel>
          <Select
            labelId="student-select-label"
            value={selectedStudentId}
            label="学生を選択 (名前 - 合計視聴時間 (進捗率))"
            onChange={(e) => setSelectedStudentId(e.target.value)}
            sx={{ color: "#fff", backgroundColor: "#333", '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[700], }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[500], }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main, }, '& .MuiSvgIcon-root': { color: theme.palette.grey[400], } }}
            MenuProps={{ PaperProps: { sx: { backgroundColor: "#333", color: "#fff", } } }}
          >
            <MenuItem value="" sx={{ '&:hover': { backgroundColor: theme.palette.grey[700] }}}>選択解除</MenuItem>
            {studentOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={{ '&:hover': { backgroundColor: theme.palette.grey[700] }}}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {!selectedStudentId && !message && allEventsForVideo.length > 0 && (<Typography color="text.secondary" sx={{ mb: 2 }}>プルダウンから学生を選択して詳細を確認してください。</Typography>)}
        {!message && allEventsForVideo.length === 0 && (<Typography color="text.secondary" sx={{ mb: 2 }}>この動画の視聴ログはまだありません。</Typography>)}

        {selectedStudentId && selectedStudentEvents && selectedStudentEvents.length > 0 && (
          <>
            <Box sx={{ mb: 2, p: 2, border: `1px solid ${theme.palette.grey[700]}`, borderRadius: 1, backgroundColor: "#1e1e1e" }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" sx={{color: "#fff"}}>選択中の学生:{" "}{ studentOptions.find((s) => String(s.value) === String(selectedStudentId))?.name || `ID: ${selectedStudentId}` }</Typography>
                  <Typography variant="body1" sx={{color: "#fff"}}>合計視聴時間: {formatTime(selectedStudentTotalTime)}</Typography>
                  {lastViewedTs && (<Typography variant="body2" color="text.secondary">最終視聴: {formatTimestampJST(lastViewedTs.toISOString())}</Typography>)}
                </Grid>
                <Grid item xs={12} md={4}>
                  {currentVideoDurationForGantt && ( // videoInfo.duration を使用
                    <>
                      <LinearProgress variant="determinate" value={selectedStudentProgress} sx={{ height: 10, borderRadius: 5, mb: 0.5 }} />
                      <Typography variant="body2" color="text.secondary" textAlign="right">{selectedStudentProgress}% 視聴済み</Typography>
                    </>
                  )}
                </Grid>
              </Grid>
              {selectedStudentId && (<MuiButton variant="outlined" size="small" component={Link} to={`/teacher/student/${selectedStudentId}/history`} sx={{ textTransform: "none", mt: 1, color: theme.palette.grey[300], borderColor: theme.palette.grey[700] }} startIcon={<Group />}>この学生の全視聴履歴を見る</MuiButton>)}
            </Box>

            {showGantt && (
              <Box sx={{ height: "auto", overflowY: "auto", mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{color: "#fff"}}>視聴パターン (ガントチャート)</Typography>
                <WatchGanttChart logs={selectedStudentEvents} videoDuration={currentVideoDurationForGantt} />
              </Box>
            )}

            {selectedStudentNotes.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: "flex", alignItems: "center", color: "#fff" }}><Notes sx={{ mr: 1, color: theme.palette.primary.main }} />ノート一覧 ({selectedStudentNotes.length}件)</Typography>
                <List dense component={Paper} variant="outlined" sx={{ backgroundColor: "#1e1e1e", color: "#fff" }}>
                  {selectedStudentNotes.map((note) => (
                    <ListItem key={note.id} divider sx={{borderColor: theme.palette.grey[700]}}>
                      <ListItemText primaryTypographyProps={{ sx: { color: "#fff" } }} secondaryTypographyProps={{ sx: { color: theme.palette.grey[400] } }} primary={<><strong>@{formatTime(note.played_seconds ?? 0)}:</strong>{" "}{note.note_text}</>} secondary={formatTimestampJST(note.timestamp)} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            
            <Typography variant="subtitle1" gutterBottom sx={{color: "#fff"}}>詳細イベントログ</Typography>
            <List dense component={Paper} variant="outlined" sx={{ backgroundColor: "#1e1e1e", color: "#fff", maxHeight: 400, overflowY: "auto", borderColor: theme.palette.grey[700] }}>
              {selectedStudentEvents.map((e, idx) => {
                const prev = idx > 0 ? selectedStudentEvents[idx - 1] : null;
                return (
                  <ListItem key={e.id} divider sx={{ borderColor: theme.palette.grey[800], display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, }}>
                    <Box sx={{color: "#fff"}}>{renderOperation(e, prev)}</Box>
                    <Typography variant="caption" sx={{ whiteSpace: "nowrap", ml: 1, color: theme.palette.grey[400] }}>{formatTimestampJST(e.timestamp)}</Typography>
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
        {selectedStudentId && (!selectedStudentEvents || selectedStudentEvents.length === 0) && !message && (
            <Typography color="text.secondary" sx={{ mb: 2 }}>選択された学生のこの動画に関する視聴ログはありません。</Typography>
        )}
      </Paper>
    </Container>
  );
}